import type { MouseEvent } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useDeleteInstructionMutation } from '@/features/instructions/instructionsApi'
import { errorMessage } from '@/features/instructions/lib/errorMessage'
import type { Instruction } from '@/features/instructions/types'
import { useMe } from '@/features/auth/useMe'
import { DeleteInstructionDialog } from '@/features/instructions/components/DeleteInstructionDialog'
import { WebInstructionCard } from '@/features/instructions/components/editor/preview/web/WebInstructionCard'
import { CoverShapes } from '@/components/catalog/CoverPlaceholder'
import { InstructionTileControls } from './InstructionTileControls'
import { AccessBadge } from './AccessBadge'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  instruction: Instruction
}

export const InstructionTile = ({ instruction }: Props) => {
  const navigate = useNavigate()
  const m = useMessages(messages)
  const { data: me } = useMe()
  const [deleteInstruction] = useDeleteInstructionMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Удаляемая плитка пропадает сразу, не дожидаясь сервера; отказ — возвращаем её.
  const [hidden, setHidden] = useState(false)

  const open = () => navigate(`/instructions/${instruction.slug}`)

  const askDelete = (e: MouseEvent) => {
    e.stopPropagation()
    setConfirmOpen(true)
  }

  const confirmDelete = async () => {
    setConfirmOpen(false)
    setHidden(true)
    try {
      await deleteInstruction(instruction.id).unwrap()
      toast.success(m.deleted)
    } catch (err) {
      setHidden(false)
      toast.error(errorMessage(err))
    }
  }

  const authorName = instruction.author?.name ?? me?.username ?? m.author
  const authorAvatar = instruction.author?.avatar ?? me?.avatar ?? null
  const coverProps = {
    title: instruction.title || m.newInstruction,
    gradient: instruction.gradient,
    cardImage: instruction.cardImage,
    titleColor: instruction.titleColor,
    authorName,
    authorAvatar,
    viewsCount: instruction.viewsCount ?? 0,
  }

  if (hidden) return null

  return (
    <>
      <div
        onClick={open}
        className="group relative text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-2xl transition-transform duration-300 hover:scale-[1.02] aspect-[4/5] [container-type:inline-size]"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            open()
          }
        }}
      >
        <AccessBadge accessType={instruction.accessType} />
        <InstructionTileControls onOpen={open} onAskDelete={askDelete} />
        <WebInstructionCard
          coverType={instruction.coverType === 'type2' ? 'type2' : 'type1'}
          showHeart={false}
          // Без картинки — наш узор из фигур, как на обложке курса без баннера.
          placeholder={<CoverShapes />}
          {...coverProps}
        />
      </div>
      <DeleteInstructionDialog
        open={confirmOpen}
        title={instruction.title}
        onOpenChange={setConfirmOpen}
        onConfirm={confirmDelete}
      />
    </>
  )
}
